/**
 * The one in-article call to action, chosen by the article's topic.
 *
 * An article used to carry five conversion modules: a sticky rail card, two
 * injected inline CTAs, a promo strip on AI SEO posts, and a newsletter band —
 * five links to the same offer, three of them under the identical headline
 * "Find out what your site is losing." The rail card and the first inline CTA
 * were on screen at the same time saying the same thing.
 *
 * The rule now is one module per scroll-screen and at most three per article:
 * a quiet text link under the table of contents, this contextual module at the
 * natural pause mid-article, and one module at the end. Matching the offer to
 * what the reader is actually reading about earns the interruption — someone
 * reading about AI search wants the visibility checker, not a generic audit.
 *
 * Pure data so the mapping is testable and a new category fails loudly in a
 * test rather than silently falling through to the generic offer.
 */

import { VIDEO_REVIEW_URL } from "./cta";

export interface BlogCta {
  /** Eyebrow above the heading. */
  label: string;
  heading: string;
  body: string;
  href: string;
  /** Button text. */
  action: string;
  /**
   * When set, the module renders the tool's own input in the column instead of
   * a link to it. A reader who has just read why AI citation matters can check
   * their own domain without leaving the article — which makes the module part
   * of the piece rather than an advert wedged into it.
   */
  tool?: "visibility" | "domain-rating";
}

/** Shown when an article's categories match nothing more specific. */
export const DEFAULT_CTA: BlogCta = {
  label: "Free · No obligation",
  heading: "Find out what your site is losing in organic revenue.",
  body: "A free personalized video review showing exactly what's holding your rankings back, and what fixing it is worth.",
  href: VIDEO_REVIEW_URL,
  action: "Get my free video review",
};

/**
 * Category slug → the offer that belongs beside that subject. Ordered by
 * specificity: the first category on a post that appears here wins.
 */
export const CTA_BY_CATEGORY: Record<string, BlogCta> = {
  "ai-seo": {
    label: "Free tool · 2 minutes",
    heading: "Is your brand showing up in AI search?",
    body: "Check whether ChatGPT, Perplexity, Google AI Overviews and Gemini mention you, and see what to fix if they don't.",
    href: "/tools/llm-visibility-checker",
    action: "Check your AI visibility",
    tool: "visibility",
  },
  "claude-seo": {
    label: "Free tool · 2 minutes",
    heading: "Is your brand showing up in AI answers?",
    body: "See whether the major AI assistants cite you today, which ones miss you, and what it takes to get quoted.",
    href: "/tools/llm-visibility-checker",
    action: "Check your AI visibility",
    tool: "visibility",
  },
  "website-authority": {
    label: "Free tool · Instant",
    heading: "What's your domain rating right now?",
    body: "Check your authority score against your competitors, and see which gaps are holding the number down.",
    href: "/tools/domain-rating-checker",
    action: "Check your domain rating",
    tool: "domain-rating",
  },
  "link-building": {
    label: "Link building",
    heading: "Authority is the slowest thing to buy and the hardest to fake.",
    body: "See how we earn editorial links that move rankings, and why the cheap ones cost more in the end.",
    href: "/link-building",
    action: "See how we build links",
  },
  "technical-seo": {
    label: "Technical SEO",
    heading: "Great content never ranks on a site search engines can't read.",
    body: "Crawl budget, Core Web Vitals, indexation and architecture: the foundations the rest of your SEO stands on.",
    href: "/technical-seo",
    action: "See our technical SEO work",
  },
  "content-seo": {
    label: "Content production",
    heading: "Content that ranks, converts, and gets cited.",
    body: "Research, briefs and production built around what your buyers search for and what your business needs to own.",
    href: "/content-production",
    action: "See how we produce content",
  },
  "local-seo": {
    label: "Local SEO",
    heading: "Win the map pack in every city you serve.",
    body: "Profile optimization, citations and reviews: the signals that decide who shows up for \"near me\".",
    href: "/local-seo",
    action: "See our local SEO work",
  },
  "programmatic-seo": {
    label: "Programmatic SEO",
    heading: "Capture long-tail demand at scale, without the thin pages.",
    body: "How we build templated pages that actually get indexed and rank, instead of bloating a site.",
    href: "/programmatic-seo",
    action: "See how we scale pages",
  },
  "seo-strategy": {
    label: "SEO audit & strategy",
    heading: "An audit is a document. A strategy is a roadmap.",
    body: "We turn findings into a revenue-ranked plan your board will fund, and then we run it.",
    href: "/seo-audit-and-strategy",
    action: "See our audit & strategy",
  },
};

/**
 * Posts that carry two modules instead of one. These are long list posts that
 * get steady search clicks (Search Console, Oct 2026) from readers doing their
 * own link building: they get the DR checker where the list starts and the
 * link-building offer before the practical section near the end. Placement is
 * insertListCtaSlots in blogCtaSlot.ts.
 */
export const LIST_POST_CTAS: Record<string, readonly [BlogCta, BlogCta]> = {
  "high-domain-authority-sites-for-backlinks": [
    {
      label: "Free tool · Instant",
      heading: "Check your own domain rating before you start.",
      body: "See where your site stands today, so you know which of these sites carry more authority than yours and can tell whether the links you add move the number.",
      href: "/tools/domain-rating-checker",
      action: "Check your domain rating",
      tool: "domain-rating",
    },
    {
      label: "Link building",
      heading: "Profiles and listings are the floor, not the ceiling.",
      body: "The sites on this list are worth claiming, but rankings move on editorial links from relevant sites. See how we earn those for clients.",
      href: "/link-building",
      action: "See how we build links",
    },
  ],
  "directory-submission-sites-list": [
    {
      label: "Free tool · Instant",
      heading: "Know your domain rating before you submit.",
      body: "Check your DR now and again after your listings go live, so you can see what directory links actually do for your site.",
      href: "/tools/domain-rating-checker",
      action: "Check your domain rating",
      tool: "domain-rating",
    },
    {
      label: "Link building",
      heading: "Directories make you findable. Editorial links make you rank.",
      body: "Once your listings are in place, the next step is earning links from sites your buyers read. See how we do that for clients.",
      href: "/link-building",
      action: "See how we build links",
    },
  ],
};

/** The offer for an article, by its categories. Never returns undefined. */
export function ctaForCategories(categories: readonly string[]): BlogCta {
  for (const slug of categories) {
    const cta = CTA_BY_CATEGORY[slug];
    if (cta) return cta;
  }
  return DEFAULT_CTA;
}

/**
 * The end-of-article module. It carries the newsletter rather than the offer,
 * so the reader who finished the piece is asked for something smaller than the
 * reader who was interrupted halfway through it.
 *
 * The promise is deliberately narrow — one idea about revenue, once a week —
 * because that is what the newsletter actually sends. "Insights", "tactics"
 * and "no filler" describe every marketing email ever written, and a signup
 * won against a vague promise unsubscribes at the first issue that keeps it.
 */
export const END_OF_ARTICLE = {
  label: "One email a week",
  heading: "How to turn search into revenue.",
  body: "One idea a week from live client work: what we changed, what it was worth, and what you can take from it.",
} as const;
